import type { ChangeEvent } from 'react'
import { useCallback, useState } from 'react'

import type { DefaultFilterSelectProps, SelectCapitalizeProps } from '@/types/components/select'
import { isMobileUserAgent } from '@/utils/browser/isMobileUserAgent'
import { cn } from '@/utils/cn'
import type { PopoverProps } from '@radix-ui/react-popover'
import { Input } from '../common/input'
import { PopoverContent, PopoverList } from '../common/popover'
import { ScrollArea } from '../common/scrollArea'
import { SearchIcon } from '../icons/search'
import { SelectItem } from './selectItem'

type FilterListContentProps = Omit<DefaultFilterSelectProps, 'className'> & SelectCapitalizeProps

export const FilterListContent = ({ items, scrollClassName, searchNormalizer, ...props }: FilterListContentProps) => {
  const [search, setSearch] = useState<string>('')
  const onSearchChange = useCallback((event: ChangeEvent<HTMLInputElement>) => setSearch(event.target.value), [])

  const filteredItems = items.filter((item) => {
    if (search === '') return true

    const lowerItem = item.toLowerCase()
    const lowerSearch = search.toLowerCase()

    if (lowerItem.includes(lowerSearch)) return true

    if (searchNormalizer) {
      return searchNormalizer(lowerItem).includes(lowerSearch)
    }

    return false
  })

  return (
    <>
      <div className="px-3 py-2">
        <div className="relative inline-flex w-full items-center">
          <SearchIcon size={16} className="pointer-events-none absolute left-3 select-none text-body-foreground" />
          <Input
            placeholder={`Find ${props.name}`}
            onChange={onSearchChange}
            value={search}
            inputMode="text"
            className="w-full rounded-xl bg-page pl-9 ring-inset"
          />
        </div>
      </div>
      <ScrollArea
        type="auto"
        className={cn(
          'max-h-40 min-h-10 border-t [&_[data-role=scroll-area-viewport]]:max-h-[inherit]',
          scrollClassName
        )}
      >
        <PopoverList>
          {filteredItems.map((item) => (
            <SelectItem key={item} item={item} {...props} />
          ))}
        </PopoverList>
      </ScrollArea>
    </>
  )
}

export const FilterSelectContent = ({
  items,
  className,
  scrollClassName,
  modal,
  searchNormalizer,
  ...props
}: DefaultFilterSelectProps & SelectCapitalizeProps & Pick<PopoverProps, 'modal'>) => {
  function handleAutoFocus(e: Event) {
    if (isMobileUserAgent()) {
      return e.preventDefault()
    }
  }

  return (
    <PopoverContent onOpenAutoFocus={handleAutoFocus} className={cn('w-auto bg-surface', className)}>
      <FilterListContent
        items={items}
        scrollClassName={scrollClassName}
        searchNormalizer={searchNormalizer}
        {...props}
      />
    </PopoverContent>
  )
}
